import { FEEDER_FIELDS, type FeederConfig } from '../feederFields';
import type { FieldName } from '../fields';
import { useFeederConfig } from '../hooks/useFeederConfig';
import { t } from '../i18n';
import { SOURCE_IDS, sendsField, type SourceId } from '../sources';

/**
 * The source picker that sits ON the condition row (PLAN-v2 §1.3), not in a separate panel:
 * the question "which switch does this condition work on?" belongs next to the field it is
 * asked about.
 *
 * A source that cannot carry this field is NOT rendered — not rendered greyed out. A
 * disabled control still says "this is a thing you might have", which is exactly the
 * confusion the rule asks us to remove.
 */

export function sourceIntersection(rows: readonly (readonly SourceId[])[]): SourceId[] {
  if (rows.length === 0) return [...SOURCE_IDS];
  return SOURCE_IDS.filter((id) => rows.every((row) => row.includes(id)));
}

/** The sources that can carry this field at all — the only ones worth offering. */
export function offeredFor(field: FieldName, config: FeederConfig = FEEDER_FIELDS): SourceId[] {
  return SOURCE_IDS.filter((id) => sendsField(id, field, config));
}

export function SourceButtons({
  field,
  value,
  onChange,
  rowLabel,
  disabled = false,
}: {
  field: FieldName;
  value: readonly SourceId[];
  onChange: (next: SourceId[]) => void;
  /** e.g. "Row 1" — keeps two builders on one screen distinguishable. */
  rowLabel: string;
  /** Shown but not switchable — an action row after the first follows row 1's sources. */
  disabled?: boolean;
}) {
  const offered = offeredFor(field, useFeederConfig());

  const toggle = (id: SourceId) => {
    onChange(value.includes(id) ? value.filter((s) => s !== id) : [...value, id]);
  };

  return (
    <span className="clause-src">
      {offered.map((id) => {
        const pressed = value.includes(id);
        return (
          <button
            key={id}
            type="button"
            className="src-dot"
            aria-pressed={pressed}
            aria-label={t('clause.src.aria', { row: rowLabel, source: id })}
            disabled={disabled}
            onClick={() => toggle(id)}
            title={disabled ? undefined : pressed ? t('clause.src.remove.tip', { source: id }) : t('clause.src.add.tip', { source: id })}
          >
            {t(`source.${id}.label`)}
          </button>
        );
      })}
    </span>
  );
}
